// ================================================================
// 管理員佈局組件
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：管理後台統一佈局
// ================================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Receipt,
  History,
  CurrencyExchange,
  Settings,
  Logout,
  ChevronLeft,
  AdminPanelSettings,
  QuestionAnswer,
  PriceChange,
  Campaign,
  Backup,
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import ChangePasswordDialog from './ChangePasswordDialog';

const DRAWER_WIDTH = 240;

interface AdminLayoutProps {
  children: React.ReactNode;
}

// ================================================================
// 選單項目定義
// ================================================================

interface MenuItemDef {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles?: string[];
}

const menuItems: MenuItemDef[] = [
  { text: '儀表板', icon: <Dashboard />, path: '/admin/dashboard' },
  { text: '使用者管理', icon: <People />, path: '/admin/users' },
  { text: '交易紀錄', icon: <Receipt />, path: '/admin/transactions' },
  { text: '問題管理', icon: <QuestionAnswer />, path: '/admin/questions' },
  { text: '價格管理', icon: <PriceChange />, path: '/admin/price' },
  { text: '公告管理', icon: <Campaign />, path: '/admin/announcements' },
  { text: '管理員日誌', icon: <History />, path: '/admin/logs' },
  { text: '匯率管理', icon: <CurrencyExchange />, path: '/admin/currencies' },
  { text: '備份管理', icon: <Backup />, path: '/admin/backup', roles: ['super_admin', 'admin'] },
  { text: '系統設定', icon: <Settings />, path: '/admin/settings', roles: ['super_admin'] },
];

// ================================================================
// 主要組件
// ================================================================

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { admin, logout } = useAdmin();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  // 處理抽屜開關
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // 處理選單點擊
  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // 處理使用者選單
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // 處理登出
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/admin/login');
  };

  // 過濾選單項目（根據角色）
  const filteredMenuItems = menuItems.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(admin?.role || '');
  });

  // 抽屜內容
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo 區域 */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <AdminPanelSettings sx={{ fontSize: 32 }} />
        <Typography variant="h6" fontWeight="bold">
          管理後台
        </Typography>
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ ml: 'auto', color: 'white' }}
          >
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* 選單列表 */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuClick(item.path)}
              sx={{
                mx: 1,
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* 使用者資訊 */}
      <Box sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {admin?.username?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {admin?.full_name || admin?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {admin?.role === 'super_admin'
                ? '超級管理員'
                : admin?.role === 'admin'
                ? '管理員'
                : '客服人員'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            好運紫微斗數
          </Typography>

          <IconButton color="inherit" onClick={handleUserMenuOpen}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: 'primary.main' }}>
              {admin?.username?.[0]?.toUpperCase() || 'A'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { handleUserMenuClose(); setPasswordDialogOpen(true); }}>
              <Settings sx={{ mr: 1 }} fontSize="small" />
              修改密碼
            </MenuItem>
            <MenuItem onClick={() => { handleUserMenuClose(); navigate('/'); }}>返回首頁</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} fontSize="small" />
              登出
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer - Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Drawer - Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* 主要內容區域 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // 允許 flex 項目收縮
          p: 3,
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` }, // 左邊距等於抽屜寬度
          mt: '64px',
          bgcolor: 'grey.50',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>

      {/* 修改密碼對話框 */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      />
    </Box>
  );
};

export default AdminLayout;
